/* eslint-disable react/jsx-no-useless-fragment */
import { FC, useCallback, useEffect, useState } from 'react';
import { useChartTableGql } from '../../graphql';
import {
  ACTIVE_DAYS_CHARTS,
  AuthorIdFilter,
  CONTRIBUTOR_CHART_KEYS,
  DEPENDENCY_VULNERABILITIES_CHART_KEYS,
  ISSUE_CHART_KEYS,
  MEMBER_CHART_KEYS,
  QUALITY_CHART_KEYS,
  TeamFilter,
  TEST_RUN_CHARTS,
  TEST_DETAIL_CHARTS,
  WELLBEING_CHART_KEYS,
} from '../../../shared/utils';
import ContributorsDetailsModal from './ContributorsDetailsModal';
import WellbeingMemberDetailsModal, {
  WellbeingMemberItem,
} from './WellbeingMemberDetailsModal';
import QualityDetailsModal from './QualityDetailsModal';
import DefaultDetailsModal from './DefaultDetailsModal';
import ActiveDaysDetailsModal from './ActiveDaysDetailsModal';
import DependencyVulnerabilitySidebar from './dependencyVulnerabilitySidebar';
import TestRunDetailsModal from './TestRunDetailsModal';
import type {
  AverageActiveDaysDetailItem,
  TestRunMetricDetailItem,
} from '../../types/charts.types';

interface MetricSidebarProps {
  openDrawer: boolean;
  handleDrawerClose: () => void;
  heading: string;
  subHeading: string;
  currentChartMeta: any;
  selectedTeam?: string;
  filterBy: AuthorIdFilter | TeamFilter;
  payload: any;
  querykey?: string;
  repoTitle?: boolean;
  hideMoreDetails?: boolean;
}

export const MetricSidebar: FC<MetricSidebarProps> = ({
  openDrawer,
  handleDrawerClose,
  heading,
  subHeading,
  currentChartMeta,
  selectedTeam,
  filterBy,
  payload,
  querykey,
  repoTitle,
  hideMoreDetails,
}) => {
  const [currentPage, setCurrentPage] = useState<number>(0);
  const startDate =
    payload && Array.isArray(payload)
      ? payload[0].payload.startDate
      : payload?.payload?.startDate;
  const endDate =
    payload && Array.isArray(payload)
      ? payload[0].payload.endDate
      : payload?.payload?.endDate;

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useChartTableGql(
      startDate,
      endDate,
      currentChartMeta?.chartKey === 'TOP_REVIEWER_DISTRIBUTION'
        ? 'PR_REVIEWER_DISTRIBUTION'
        : currentChartMeta?.chartKey === 'PR_CYCLE_TIME_BREAKDOWN'
        ? 'PR_CYCLE_TIME'
        : currentChartMeta?.chartKey === 'DEPLOYMENT_FREQUENCY_DISTRIBUTION'
        ? 'DEPLOYMENT_FREQUENCY'
        : currentChartMeta?.chartKey === 'LEAD_TIME_BREAKDOWN'
        ? 'LEAD_TIME_FOR_CHANGE'
        : currentChartMeta?.chartKey === 'MEAN_TIME_TO_RECOVERY_DISTRIBUTION'
        ? 'MEAN_TIME_TO_RECOVERY'
        : currentChartMeta?.chartKey === 'CHANGE_HEALTH'
        ? 'CHANGE_FAILURE_RATE'
        : currentChartMeta?.chartKey === 'CUSTOM'
        ? currentChartMeta?.chartTitle
        : payload?.name?.includes('New') &&
          QUALITY_CHART_KEYS?.includes(currentChartMeta?.chartKey)
        ? `NEW_${currentChartMeta?.chartKey}`
        : ISSUE_CHART_KEYS?.includes(currentChartMeta?.chartKey) &&
          payload?.name === 'Total Issues'
        ? 'ISSUE_UPDATED'
        : ISSUE_CHART_KEYS?.includes(currentChartMeta?.chartKey) &&
          payload?.name === 'Reopened Issues'
        ? 'ISSUE_REOPENED'
        : currentChartMeta?.chartKey,
      filterBy,
      currentPage,
      20,
      currentChartMeta?.detailEntity ? currentChartMeta.detailEntity : ''
    );

  useEffect(() => {
    setCurrentPage(0);
  }, [openDrawer]);

  const handlePageClick = useCallback(
    (page: number) => {
      setCurrentPage(page - 1);
    },
    [currentPage]
  );

  return (
    <>
      {[...CONTRIBUTOR_CHART_KEYS].includes(currentChartMeta?.chartKey) ? (
        <ContributorsDetailsModal
          openDrawer={openDrawer}
          handleDrawerClose={handleDrawerClose}
          team={selectedTeam!}
          contributorData={data}
          loading={isLoading}
          repoTitle={repoTitle}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          startDate={startDate}
          endDate={endDate}
          filterBy={filterBy}
        />
      ) : [...ACTIVE_DAYS_CHARTS].includes(currentChartMeta?.chartKey) ? (
        <ActiveDaysDetailsModal
          openDrawer={openDrawer}
          handleDrawerClose={handleDrawerClose}
          team={selectedTeam!}
          contributorData={
            data
              ? ({ data: (data as any).data ?? [] } as {
                  data: AverageActiveDaysDetailItem[];
                })
              : undefined
          }
          isFetching={isLoading}
          subHeading={subHeading}
          startDate={startDate}
          endDate={endDate}
        />
      ) : [...WELLBEING_CHART_KEYS, ...MEMBER_CHART_KEYS].includes(
          currentChartMeta?.chartKey
        ) ? (
        <WellbeingMemberDetailsModal
          openDrawer={openDrawer}
          handleDrawerClose={handleDrawerClose}
          wellbeingMemberData={
            data as
              | {
                  data: WellbeingMemberItem[];
                  currentPage?: number;
                  totalPages?: number;
                }
              | undefined
          }
          heading={heading}
          subHeading={subHeading}
          isFetching={isLoading}
          handlePageClick={handlePageClick}
        />
      ) : [...QUALITY_CHART_KEYS].includes(currentChartMeta?.chartKey) ? (
        <QualityDetailsModal
          openDrawer={openDrawer}
          handleDrawerClose={handleDrawerClose}
          qualityData={
            data as
              | { data: any[]; currentPage?: number; totalPages?: number }
              | undefined
          }
          heading={heading}
          subHeading={subHeading}
          isFetching={isLoading}
          handlePageClick={handlePageClick}
        />
      ) : [...DEPENDENCY_VULNERABILITIES_CHART_KEYS].includes(
          currentChartMeta?.chartKey
        ) ? (
        <DependencyVulnerabilitySidebar
          openDrawer={openDrawer}
          handleDrawerClose={handleDrawerClose}
          dependencyVulnerabilityData={data}
          heading={heading}
          subHeading={subHeading}
          isFetching={isLoading}
          handlePageClick={handlePageClick}
          filterBy={filterBy}
          startDate={startDate}
          endDate={endDate}
        />
      ) : [...TEST_RUN_CHARTS, ...TEST_DETAIL_CHARTS].includes(
          currentChartMeta?.chartKey
        ) ? (
        <TestRunDetailsModal
          payload={payload}
          openDrawer={openDrawer}
          handleDrawerClose={handleDrawerClose}
          defaultData={data as { data: TestRunMetricDetailItem[] } | undefined}
          heading={heading}
          subHeading={subHeading}
          isFetching={isLoading}
          handlePageClick={handlePageClick}
          currentChartMeta={currentChartMeta}
          querykey={querykey}
          startDate={startDate}
          endDate={endDate}
          filterBy={filterBy}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
        />
      ) : (
        <DefaultDetailsModal
          payload={payload}
          openDrawer={openDrawer}
          handleDrawerClose={handleDrawerClose}
          defaultData={data}
          heading={heading}
          subHeading={subHeading}
          isFetching={isLoading}
          handlePageClick={handlePageClick}
          currentChartMeta={currentChartMeta}
          querykey={querykey}
          startDate={startDate}
          endDate={endDate}
          filterBy={filterBy}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          hideMoreDetails={hideMoreDetails}
        />
      )}
    </>
  );
};

export default MetricSidebar;
